// Theme axis helpers for the Automations frontends.
//
// The CSS cascade (./index.css) reacts to two attributes on <html>:
//   data-theme="dark" | "light"        — colour palette (dark is canonical)
//   data-density="comfortable" | "compact"
//
// The light/dark preference is persisted in a COOKIE (not localStorage) so it
// is available to the very first paint of every page — including the login
// page — and could be read server-side / at the edge if ever needed. This
// mirrors how ServiceAI keeps the theme available before the app boots (it
// uses a localStorage key for the same purpose; we use a cookie because Jeff
// wants the login page to render with the right theme from the cookie).
//
// Density stays in localStorage — it is not needed before login.

export type ThemeMode = 'dark' | 'light';
export type Density = 'comfortable' | 'compact';

export const THEME_COOKIE = 'crauto-theme';
const DENSITY_KEY = 'crauto-density';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function readThemeCookie(): ThemeMode | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${THEME_COOKIE}=(dark|light)(?:;|$)`),
  );
  return match ? (match[1] as ThemeMode) : null;
}

function writeThemeCookie(mode: ThemeMode): void {
  // Not HttpOnly: the first-paint inline script and these helpers must read it
  // client-side. SameSite=Lax is enough — it is a cosmetic preference, not a
  // credential. Secure only over HTTPS so local http dev still works.
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${THEME_COOKIE}=${mode}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
}

export function applyTheme(mode: ThemeMode): void {
  document.documentElement.setAttribute('data-theme', mode);
  writeThemeCookie(mode);
}

/**
 * Apply a palette WITHOUT persisting it — for an embedded host (the Teams personal
 * tab) whose theme comes from the host, not from the user's cookie. Adds the
 * high-contrast palette, which only the Teams tab uses.
 */
export function applyEmbeddedTheme(mode: ThemeMode | 'hc'): void {
  document.documentElement.setAttribute('data-theme', mode);
}

export function getStoredTheme(): ThemeMode | null {
  return readThemeCookie();
}

export function applyDensity(density: Density): void {
  document.documentElement.setAttribute('data-density', density);
  try {
    localStorage.setItem(DENSITY_KEY, density);
  } catch {
    /* storage may be unavailable (private mode); the attribute still applies */
  }
}

// Resolve the initial theme: cookie → app default. Call once at boot.
// `fallback` lets each app pick its own default (customer + admin default
// light to match their existing shells; the design's canonical mode is dark).
export function initTheme(fallback: ThemeMode = 'light'): ThemeMode {
  const mode = readThemeCookie() ?? fallback;
  applyTheme(mode);
  try {
    const d = localStorage.getItem(DENSITY_KEY);
    if (d === 'compact' || d === 'comfortable') {
      document.documentElement.setAttribute('data-density', d);
    }
  } catch {
    /* ignore */
  }
  return mode;
}
